import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import {
  CATEGORIES,
  getDesigns as defaultGetDesigns,
  getCategory as defaultGetCategory,
  searchDesigns as defaultSearchDesigns,
  getDesignByParts as defaultGetDesignByParts,
} from "../data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// -------- helpers --------
const absolutize = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

const findCat = (slug) => CATEGORIES.find((c) => c.slug === slug) || null;

const labelFor = (catSlug, subSlug, customSubs) => {
  const c = findCat(catSlug);
  const sub = c && c.subCategories.find((s) => s.slug === subSlug);
  if (sub) return { catLabel: c.label, subLabel: sub.label };
  const cs = customSubs.find(
    (s) => s.parentCategory === catSlug && s.slug === subSlug
  );
  return {
    catLabel: c ? c.label : catSlug,
    subLabel: cs ? cs.label : subSlug,
  };
};

/**
 * Normalise a raw admin design doc into the same shape as the built-in ones,
 * so the same UI components can render either.
 */
const normaliseCustom = (raw, customSubs) => {
  const { catLabel, subLabel } = labelFor(raw.category, raw.subCategory, customSubs);
  // "number" for a custom item = the short hash tail from its id (custom-cat-sub-XXXXXXXX)
  const idParts = (raw.id || "").split("-");
  const number = idParts.length >= 4 ? idParts[idParts.length - 1] : "000";
  const image = absolutize(raw.image || "");
  const gallery = (raw.gallery && raw.gallery.length ? raw.gallery : [raw.image])
    .filter(Boolean)
    .map(absolutize);
  return {
    id: raw.id,
    number,
    name: raw.name,
    styleName: raw.styleName || raw.name || "",
    image,
    gallery,
    video: raw.video ? absolutize(raw.video) : "",
    weight: Number(raw.weight) || 0,
    weightUnit: raw.weightUnit || "g",
    purity: raw.purity || "",
    hallmark: raw.hallmark || "BIS Hallmark 916",
    makingCharge: raw.makingCharge || "On request",
    priceNote: "Price on enquiry — subject to daily rate",
    description: raw.description || "",
    category: { slug: raw.category, label: catLabel },
    subCategory: { slug: raw.subCategory, label: subLabel },
    gender: raw.gender || "unisex",
    isExclusive: !!raw.isExclusive,
    limitedCollectionId: raw.limitedCollectionId || null,
    isCustom: true,
  };
};

// -------- context --------
const CatalogContext = createContext(null);

export function CatalogProvider({ children }) {
  const [overlay, setOverlay] = useState({
    designs: [],
    subcategories: [],
    hidden: [],
    storeSettings: null,
    homeItems: [],
    hero: null,
    limitedCollections: [],
    limitedScopedDesigns: [],
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/catalog/custom`);
      setOverlay({
        designs: Array.isArray(data.designs) ? data.designs : [],
        subcategories: Array.isArray(data.subcategories) ? data.subcategories : [],
        hidden: Array.isArray(data.hidden) ? data.hidden : [],
        storeSettings: data.storeSettings || null,
        homeItems: Array.isArray(data.homeItems) ? data.homeItems : [],
        hero: data.hero || null,
        limitedCollections: Array.isArray(data.limitedCollections) ? data.limitedCollections : [],
        limitedScopedDesigns: Array.isArray(data.limitedScopedDesigns) ? data.limitedScopedDesigns : [],
      });
    } catch (_) {
      // stay with previous overlay; public site still works from defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // -------- derived helpers --------
  const hideDefaults = !!(overlay.storeSettings && overlay.storeSettings.hideDefaults);

  const customDesignsAll = useMemo(
    () => overlay.designs.map((d) => normaliseCustom(d, overlay.subcategories)),
    [overlay.designs, overlay.subcategories]
  );

  const limitedScopedDesignsAll = useMemo(
    () => overlay.limitedScopedDesigns.map((d) => normaliseCustom(d, overlay.subcategories)),
    [overlay.limitedScopedDesigns, overlay.subcategories]
  );

  const hiddenSet = useMemo(() => new Set(overlay.hidden || []), [overlay.hidden]);

  // Merged categories (default + custom sub-categories, filtered if hideDefaults)
  const categoriesMerged = useMemo(() => {
    return CATEGORIES.map((cat) => {
      const customSubsForCat = overlay.subcategories.filter(
        (s) => s.parentCategory === cat.slug
      );
      const extraSubs = customSubsForCat.map((s) => ({
        slug: s.slug,
        label: s.label,
        images: [],
        styleNames: ["Custom"],
        isCustom: true,
      }));
      let subs = [...cat.subCategories, ...extraSubs];

      if (hideDefaults) {
        // keep only subs that have at least one custom design
        subs = subs.filter((s) =>
          customDesignsAll.some(
            (d) => d.category.slug === cat.slug && d.subCategory.slug === s.slug
          )
        );
      }
      return { ...cat, subCategories: subs };
    }).filter((cat) => cat.subCategories.length > 0);
  }, [customDesignsAll, hideDefaults, overlay.subcategories]);

  const getCategoryMerged = useCallback(
    (slug) => categoriesMerged.find((c) => c.slug === slug) || null,
    [categoriesMerged]
  );

  const getDesignsMerged = useCallback(
    (catSlug, subSlug) => {
      const defaults = hideDefaults ? [] : defaultGetDesigns(catSlug, subSlug);
      const visibleDefaults = defaults.filter((d) => !hiddenSet.has(d.id));
      const customs = customDesignsAll.filter(
        (d) => d.category.slug === catSlug && d.subCategory.slug === subSlug
      );
      // customs first so the admin's newest work leads the grid
      return [...customs, ...visibleDefaults];
    },
    [customDesignsAll, hiddenSet, hideDefaults]
  );

  const getDesignByPartsMerged = useCallback(
    (catSlug, subSlug, number) => {
      const custom = customDesignsAll.find(
        (d) =>
          d.category.slug === catSlug &&
          d.subCategory.slug === subSlug &&
          d.number === number
      );
      if (custom) return custom;
      // Include limited-collection scoped designs so their detail pages open.
      const scoped = limitedScopedDesignsAll.find(
        (d) =>
          d.category.slug === catSlug &&
          d.subCategory.slug === subSlug &&
          d.number === number
      );
      if (scoped) return scoped;
      if (hideDefaults) return null;
      const d = defaultGetDesignByParts(catSlug, subSlug, number);
      if (d && hiddenSet.has(d.id)) return null;
      return d;
    },
    [customDesignsAll, limitedScopedDesignsAll, hiddenSet, hideDefaults]
  );

  const searchDesignsMerged = useCallback(
    (query, limit = 60) => {
      const q = query.trim().toLowerCase();
      if (!q) return [];
      const results = [];
      for (const d of customDesignsAll) {
        const hay = `${d.name} ${d.styleName} ${d.subCategory.label} ${d.category.label}`.toLowerCase();
        if (hay.includes(q)) {
          results.push(d);
          if (results.length >= limit) return results;
        }
      }
      if (!hideDefaults) {
        const defaults = defaultSearchDesigns(query, limit);
        for (const d of defaults) {
          if (!hiddenSet.has(d.id)) {
            results.push(d);
            if (results.length >= limit) return results;
          }
        }
      }
      return results;
    },
    [customDesignsAll, hiddenSet, hideDefaults]
  );

  // Every visible design across every category/sub-category — used by the
  // Shopper Concierge to compute keyword-based product suggestions inline in
  // chat replies. Kept memoised so we don't rebuild it on every re-render.
  const allDesignsMerged = useMemo(() => {
    const list = [];
    for (const cat of categoriesMerged) {
      for (const sub of cat.subCategories) {
        for (const d of getDesignsMerged(cat.slug, sub.slug)) {
          list.push(d);
        }
      }
    }
    return list;
  }, [categoriesMerged, getDesignsMerged]);

  const value = useMemo(
    () => ({
      loading,
      hideDefaults,
      storeSettings: overlay.storeSettings,
      customDesigns: customDesignsAll,
      customSubcategories: overlay.subcategories,
      hiddenIds: overlay.hidden,
      categoriesMerged,
      getCategoryMerged,
      getDesignsMerged,
      getDesignByPartsMerged,
      searchDesignsMerged,
      allDesignsMerged,
      // home config
      homeItems: overlay.homeItems,
      hero: overlay.hero,
      // limited-time spotlights (enabled ones only, sorted by priority)
      limitedCollections: overlay.limitedCollections,
      limitedScopedDesigns: limitedScopedDesignsAll,
      // exclusive designs (custom designs flagged isExclusive)
      exclusiveDesigns: customDesignsAll.filter(
        (d) => overlay.designs.find((raw) => raw.id === d.id)?.isExclusive
      ),
      refresh,
    }),
    [
      loading,
      hideDefaults,
      overlay.storeSettings,
      overlay.subcategories,
      overlay.hidden,
      overlay.homeItems,
      overlay.hero,
      overlay.limitedCollections,
      limitedScopedDesignsAll,
      overlay.designs,
      customDesignsAll,
      categoriesMerged,
      getCategoryMerged,
      getDesignsMerged,
      getDesignByPartsMerged,
      searchDesignsMerged,
      allDesignsMerged,
      refresh,
    ]
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
  const ctx = useContext(CatalogContext);
  if (!ctx) {
    // Provider missing — return a no-op-ish default so components don't crash during dev.
    return {
      loading: false,
      hideDefaults: false,
      storeSettings: null,
      customDesigns: [],
      customSubcategories: [],
      hiddenIds: [],
      categoriesMerged: CATEGORIES,
      getCategoryMerged: defaultGetCategory,
      getDesignsMerged: defaultGetDesigns,
      getDesignByPartsMerged: defaultGetDesignByParts,
      searchDesignsMerged: defaultSearchDesigns,
      allDesignsMerged: [],
      homeItems: [],
      hero: null,
      limitedCollections: [],
      limitedScopedDesigns: [],
      exclusiveDesigns: [],
      refresh: () => {},
    };
  }
  return ctx;
}
